import type { CollabAutomationStatus } from "@unbox/api";
import { Edit05 } from "@untitledui/icons";
import { Focusable } from "react-aria-components";
import { type TargetCollaborationDetail, useTargetCollaborationAutomations } from "@/api/collaborations";
import { AutomationsActiveSignal, useActiveAutomationsCount } from "@/components/automations/automations-active-signal";
import { Button } from "@/components/base/buttons/button";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { RulesRunningLink } from "@/components/rules/rules-running-link";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/utils/date-conversion";
import { CollaborationStatusBadge } from "./collaboration-status-badge";
import { ProductChips } from "./product-chips";
import { RecommendedCreatorsButton } from "./recommended-creators-button";
import { ReviewSampleRequestsButton } from "./review-sample-requests-button";

type Props = {
  collaboration: TargetCollaborationDetail;
  onEdit?: () => void;
  sampleRequestedCount?: number;
  showRecommendations?: boolean;
  onViewAutomations?: () => void;
};

export const MetadataItem = ({ label, value }: { label: string; value: string }) => (
  <span className="flex items-center gap-1 whitespace-nowrap text-sm">
    <span className="text-quaternary">{label}</span>
    <span className="font-medium text-tertiary">{value}</span>
  </span>
);

export const Dot = () => <span className="size-1 shrink-0 rounded-full bg-quaternary" />;

export const AutomationsItem = ({
  automations,
  isLoading,
  isError,
  onOpen,
}: {
  automations: CollabAutomationStatus[];
  isLoading: boolean;
  isError: boolean;
  onOpen: () => void;
}) => {
  const activeAutomations = useActiveAutomationsCount(automations);

  if (isLoading) {
    return (
      <>
        <Dot />
        <Skeleton className="h-5 w-36 rounded" />
      </>
    );
  }
  if (isError) {
    return (
      <>
        <Dot />
        <span className="text-sm text-quaternary">Automations unavailable</span>
      </>
    );
  }
  if (activeAutomations === 0) return null;
  return (
    <>
      <Dot />
      <AutomationsActiveSignal variant="inline" automations={automations} onOpen={onOpen} />
    </>
  );
};

const CollaborationAutomationsItem = ({ collaborationId, onOpen }: { collaborationId: string; onOpen: () => void }) => {
  const { data: automations = [], isLoading, isError } = useTargetCollaborationAutomations(collaborationId);
  return <AutomationsItem automations={automations} isLoading={isLoading} isError={isError} onOpen={onOpen} />;
};

export const CollaborationDetailHeader = ({
  collaboration,
  onEdit,
  sampleRequestedCount = 0,
  showRecommendations = false,
  onViewAutomations,
}: Props) => {
  const typeLabel = collaboration.type === "STANDARD" ? "Target" : "Top Creator Program";

  return (
    <div className="flex flex-row justify-between items-start gap-8 md:mb-8 lg:mb-0">
      <div className="flex flex-col gap-1.5 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <Tooltip title={collaboration.name}>
            <Focusable>
              <h1 className="inline-block min-w-0 max-w-[80ch] truncate text-left text-xl text-primary outline-hidden">
                {collaboration.name}
              </h1>
            </Focusable>
          </Tooltip>
          <CollaborationStatusBadge status={collaboration.status} />
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-12">
          <ProductChips
            products={collaboration.products.map((product) => ({
              title: product.productTitle ?? product.productId,
              commissionRate: product.commissionRate,
              imageUrl: null,
            }))}
            visible={1}
          />
          <Dot />
          <MetadataItem label="Type" value={typeLabel} />
          <Dot />
          <MetadataItem label="Expires" value={formatDate(collaboration.endTime)} />
          <Dot />
          <MetadataItem label="Free Sample" value={collaboration.hasFreeSample ? "On" : "Off"} />
          <Dot />
          <RulesRunningLink ruleCount={collaboration.matchingRules.length} />
          {onViewAutomations && (
            <CollaborationAutomationsItem collaborationId={collaboration.collaborationId} onOpen={onViewAutomations} />
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {showRecommendations && <RecommendedCreatorsButton collaborationId={collaboration.collaborationId} />}
        <ReviewSampleRequestsButton
          collaborationId={collaboration.collaborationId}
          sampleRequestedCount={sampleRequestedCount}
        />
        {onEdit && (
          <Button color="secondary" size="md" iconLeading={<Edit05 className="w-4 h-4" />} onClick={onEdit}>
            Edit
          </Button>
        )}
      </div>
    </div>
  );
};
